import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import snipeHubService from '../../utils/services/snipeHubService';
import resultsTrackerService from '../../utils/services/resultsTrackerService';
import { getTeamMeta } from './aflTeams';
import TeamGuernsey from './Guernsey';
import { BookmakerLogo, BOOKMAKER_DISPLAY_NAMES, bookmakerRank } from '../HotProps/bookmakerAssets';
import { useNavigate } from 'react-router-dom';
import CountdownRing from './CountdownRing';

const MAX_VISIBLE_LOGOS = 7;

// Auto-refresh cadence (seconds) for the matches list.
const REFRESH_SECONDS = 60;

function bookmakerLabel(b) {
    return BOOKMAKER_DISPLAY_NAMES[b?.bookmaker] || b?.display_name || b?.bookmaker || '';
}

const SPORT_OPTIONS = [
    { value: 'afl', label: 'AFL' },
];


const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Flips to `true` one frame after mount so CSS transitions have an initial
// state to animate from. Returns `true` immediately when the user prefers
// reduced motion (no animation).
function useMountAnimation() {
    const [on, setOn] = useState(false);
    useEffect(() => {
        if (prefersReducedMotion()) {
            setOn(true);
            return undefined;
        }
        const raf = requestAnimationFrame(() => setOn(true));
        return () => cancelAnimationFrame(raf);
    }, []);
    return on;
}

// Two loops of 0-9 so a freshly mounted reel spins through a full revolution
// before settling on its target digit (which lives in the second loop).
const REEL_STRIP = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

// A single slot-machine digit: a vertical strip of digits that starts at the
// top and spins down to the target. When the target digit later changes
// (units ⇄ dollars toggle, data refresh) it rolls between positions.
function ReelDigit({ digit, delay = 0 }) {
    const [pos, setPos] = useState(0);

    useEffect(() => {
        // Set the target on the next frame so the browser paints the reel at
        // its start position first — otherwise there's nothing to transition.
        const raf = requestAnimationFrame(() => setPos(10 + digit));
        return () => cancelAnimationFrame(raf);
    }, [digit]);

    return (
        <span className="inline-block overflow-hidden" style={{ height: '1em' }}>
            <span
                className="block will-change-transform"
                style={{
                    transform: `translateY(-${pos}em)`,
                    transition: `transform 1100ms cubic-bezier(0.23, 1, 0.32, 1) ${delay}ms`
                }}
            >
                {REEL_STRIP.map((d, i) => (
                    <span key={i} className="block" style={{ height: '1em', lineHeight: '1em' }}>{d}</span>
                ))}
            </span>
        </span>
    );
}

// Renders a formatted value ("$2,313", "+13.4%") with each digit as a
// spinning reel; punctuation, signs and suffixes stay static. Reels are
// staggered left→right for a cascade effect.
function ReelValue({ text }) {
    const str = String(text ?? '');
    if (prefersReducedMotion()) return <span>{str}</span>;
    return (
        <span className="inline-flex" style={{ height: '1em' }}>
            {str.split('').map((ch, i) => (
                /\d/.test(ch)
                    ? <ReelDigit key={i} digit={Number(ch)} delay={i * 55} />
                    : <span key={i} className="inline-block" style={{ height: '1em', lineHeight: '1em' }}>{ch}</span>
            ))}
        </span>
    );
}

// Small directional arrow used beside strip metrics and the headline delta.
// `trend`: 'up' (emerald), 'down' (red), or null (neutral, no icon).
function TrendArrow({ trend, className = 'h-3.5 w-3.5' }) {
    if (!trend) return null;
    const up = trend === 'up';
    return (
        <svg className={`${className} ${up ? 'text-emerald-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d={up ? 'M3 17l6-6 4 4 8-8m0 0h-5m5 0v5' : 'M3 7l6 6 4-4 8 8m0 0h-5m5 0v-5'} />
        </svg>
    );
}

// One cell of the card's bottom metric strip: leading trend arrow, bold value,
// optional sub-label, and an uppercase caption underneath.
function PerfStat({ label, value, sub, trend }) {
    return (
        <div className="flex min-w-0 flex-col items-center gap-1.5 px-3.5 py-3 md:px-4 md:py-3.5">
            <span className="truncate text-center text-[10px] font-mono uppercase tracking-[0.12em] text-slate-500">{label}</span>
            <div className="flex items-center gap-1.5">
                <TrendArrow trend={trend} />
                <span className="min-w-0 text-base font-bold tabular-nums text-white md:text-lg">{value}</span>
                {sub && <span className="shrink-0 text-[10px] font-mono text-slate-600 tabular-nums">{sub}</span>}
            </div>
        </div>
    );
}

// Wraps a full-width chart SVG with lightweight y-axis (top/bottom) and x-axis
// (left/right) tick labels. Labels are HTML so they stay crisp regardless of
// the SVG's non-uniform scaling.
function ChartAxisFrame({ yTop, yBottom, xLeft, xRight, children }) {
    return (
        <div className="flex h-full flex-col">
            <div className="flex min-h-0 flex-1">
                <div className="flex w-11 shrink-0 flex-col justify-between py-px pr-1.5 text-right text-[8px] font-mono leading-none text-slate-500">
                    <span>{yTop}</span>
                    <span>{yBottom}</span>
                </div>
                <div className="relative min-w-0 flex-1">{children}</div>
            </div>
            <div className="flex justify-between pl-11 pt-1 text-[8px] font-mono uppercase tracking-wide leading-none text-slate-500">
                <span>{xLeft}</span>
                <span>{xRight}</span>
            </div>
        </div>
    );
}

// Filled area chart for the cumulative monthly P/L series. Uses a
// non-scaling stroke so the line stays crisp while the SVG stretches to fill
// its container width (preserveAspectRatio="none").
function PerformanceAreaChart({ data, positive, height = 150, format = (v) => v, xLeft = 'Start', xRight = 'Now' }) {
    const id = useMemo(() => `perf-area-${Math.random().toString(36).slice(2, 8)}`, []);
    const animate = useMountAnimation();
    const pts = Array.isArray(data) && data.length > 1 ? data : [0, 0];
    const W = 400;
    const H = height;
    const pad = 8;
    const min = Math.min(...pts, 0);
    const max = Math.max(...pts, 0);
    const range = max - min || 1;
    const stepX = (W - pad * 2) / (pts.length - 1);
    const toX = (i) => pad + i * stepX;
    const toY = (v) => H - pad - ((v - min) / range) * (H - pad * 2);
    const line = pts.map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)} ${toY(v).toFixed(1)}`).join(' ');
    const area = `${line} L${toX(pts.length - 1).toFixed(1)} ${(H - pad).toFixed(1)} L${toX(0).toFixed(1)} ${(H - pad).toFixed(1)} Z`;
    const zeroY = toY(0);
    const color = positive ? '#10b981' : '#ef4444';

    return (
        <ChartAxisFrame yTop={format(max)} yBottom={format(min)} xLeft={xLeft} xRight={xRight}>
            <svg className="h-full w-full" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                <defs>
                    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                        <stop offset="100%" stopColor={color} stopOpacity="0" />
                    </linearGradient>
                </defs>
                {[0.25, 0.5, 0.75].map((f) => (
                    <line key={f} x1={pad} y1={pad + f * (H - pad * 2)} x2={W - pad} y2={pad + f * (H - pad * 2)} stroke="#1e293b" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                ))}
                <line x1={pad} y1={zeroY} x2={W - pad} y2={zeroY} stroke="#334155" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
                <path
                    d={area}
                    fill={`url(#${id})`}
                    style={{ opacity: animate ? 1 : 0, transition: 'opacity 700ms ease 350ms' }}
                />
                <path
                    d={line}
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    pathLength="1"
                    style={{
                        strokeDasharray: 1,
                        strokeDashoffset: animate ? 0 : 1,
                        transition: 'stroke-dashoffset 1100ms cubic-bezier(0.22, 1, 0.36, 1)'
                    }}
                />
            </svg>
        </ChartAxisFrame>
    );
}

// Per-day P/L bar chart for the month: one bar per calendar day, drawn up
// (emerald) or down (red) from a shared zero baseline. Stretches to fill its
// container width (preserveAspectRatio="none"); the dashed baseline keeps a
// crisp 1px via a non-scaling stroke.
function DailyPnlChart({ data, height = 150, format = (v) => v, labelFormat, xLeft = 'Day 1', xRight = 'Now' }) {
    const animate = useMountAnimation();
    const pts = Array.isArray(data) && data.length ? data : [0];
    const W = 400;
    const H = height;
    // Extra vertical padding leaves headroom for the value labels that sit just
    // above / below each bar without clipping at the plot edges.
    const pad = 18;
    const min = Math.min(...pts, 0);
    const max = Math.max(...pts, 0);
    const range = max - min || 1;
    const toY = (v) => H - pad - ((v - min) / range) * (H - pad * 2);
    const zeroY = toY(0);
    const slot = (W - pad * 2) / pts.length;
    const barW = Math.max(1.5, slot * 0.62);
    // Labels get crowded past ~18 bars; hide them then to stay legible.
    const showLabels = !!labelFormat && pts.length <= 18;

    return (
        <ChartAxisFrame yTop={format(max)} yBottom={format(min)} xLeft={xLeft} xRight={xRight}>
            <svg className="h-full w-full" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                {[0.25, 0.5, 0.75].map((f) => (
                    <line key={f} x1={pad} y1={pad + f * (H - pad * 2)} x2={W - pad} y2={pad + f * (H - pad * 2)} stroke="#1e293b" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                ))}
                {pts.map((v, i) => {
                    const x = pad + i * slot + (slot - barW) / 2;
                    const y = v >= 0 ? toY(v) : zeroY;
                    const barH = Math.max(0.75, Math.abs(toY(v) - zeroY));
                    return (
                        <rect
                            key={i}
                            x={x}
                            y={y}
                            width={barW}
                            height={barH}
                            fill={v >= 0 ? '#10b981' : '#ef4444'}
                            opacity="0.85"
                            style={{
                                transform: animate ? 'scaleY(1)' : 'scaleY(0)',
                                transformBox: 'fill-box',
                                transformOrigin: v >= 0 ? 'bottom' : 'top',
                                transition: `transform 650ms cubic-bezier(0.22, 1, 0.36, 1) ${Math.min(i * 22, 500)}ms`
                            }}
                        />
                    );
                })}
                <line x1={pad} y1={zeroY} x2={W - pad} y2={zeroY} stroke="#334155" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
            </svg>
            {showLabels && (
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{ opacity: animate ? 1 : 0, transition: 'opacity 500ms ease 400ms' }}
                >
                    {pts.map((v, i) => {
                        const cx = ((pad + i * slot + slot / 2) / W) * 100;
                        const cy = (toY(v) / H) * 100;
                        const positive = v >= 0;
                        return (
                            <span
                                key={i}
                                className={`absolute text-[7px] font-mono font-semibold tabular-nums leading-none ${positive ? 'text-emerald-400/90' : 'text-red-400/90'}`}
                                style={{
                                    left: `${cx}%`,
                                    top: `${cy}%`,
                                    transform: positive ? 'translate(-50%, -135%)' : 'translate(-50%, 35%)'
                                }}
                            >
                                {labelFormat(v)}
                            </span>
                        );
                    })}
                </div>
            )}
        </ChartAxisFrame>
    );
}

// The hero's monthly results module — a "terminal" panel fed by the
// backend-computed summary (`/results-tracker/performance/monthly`). Owns its
// loading / error / empty / data states so the hero layout never shifts, and
// lets the user flip the denomination between units and dollars.
function MonthlyPerformanceTerminal({ perf, isLoading, error, onRetry, display = 'units', onDisplayChange }) {
    const navigate = useNavigate();
    const label = perf?.label || new Date().toLocaleDateString([], { month: 'short' });
    const isDollars = display === 'dollars';

    const pnl = perf?.pnl ?? 0;
    const roi = perf?.roi ?? 0;
    const pnlPositive = pnl >= 0;
    const roiPositive = roi >= 0;
    const winRate = perf?.winRate ?? 0;
    const staked = perf?.staked ?? 0;
    const returned = perf?.returned ?? 0;
    const series = Array.isArray(perf?.series) ? perf.series : [];

    // Per-day P/L for the daily bar chart, from `perf.dailySeries` — an array of
    // { date, pnl } objects, one per settled day (see monthly_performance_api.md).
    const daily = useMemo(() => {
        const raw = Array.isArray(perf?.dailySeries) ? perf.dailySeries : [];
        return {
            values: raw.map((d) => (typeof d === 'number' ? d : d?.pnl ?? 0)),
            dates: raw.map((d) => (d && typeof d === 'object' ? d.date : null))
        };
    }, [perf?.dailySeries]);

    // "1 Jul" style label for a YYYY-MM-DD day (parsed at local noon to dodge
    // timezone roll-over); empty string when absent/unparseable.
    const formatDayLabel = (ds) => {
        if (!ds) return '';
        const d = new Date(`${ds}T12:00:00`);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
    };

    // Amounts stay short so the panel never outgrows the hero: full precision
    // for small values ("12.4u" / "$12.40"), thousands lose the decimals
    // ("$2,313"), and 10k+ collapses to compact notation ("$20.5K").
    const fmtNum = (v) => {
        const abs = Math.abs(v);
        if (abs >= 10000) {
            return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(abs);
        }
        if (abs >= 1000) return abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
        return isDollars ? abs.toFixed(2) : abs.toFixed(1);
    };
    const fmtAmount = (v) => (isDollars ? `$${fmtNum(v)}` : `${fmtNum(v)}u`);
    const fmtSigned = (v) => `${v < 0 ? '-' : '+'}${fmtAmount(v)}`;
    // Compact signed value for axis ticks — collapses a near-zero tick to "0".
    const fmtAxis = (v) => (Math.abs(v) < 0.05 ? (isDollars ? '$0' : '0u') : fmtSigned(v));
    // Signed value with no unit suffix — used for the per-bar labels.
    const fmtBar = (v) => `${v < 0 ? '-' : '+'}${fmtNum(v)}`;
    // Full amount with thousands separators (no compacting) for the strip.
    const fmtFull = (v) => {
        const dp = isDollars ? 2 : 1;
        const n = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
        return isDollars ? `$${n}` : `${n}u`;
    };

    const shell = 'relative flex w-full flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60';

    // Card header: "Monthly Performance" title on the left, units/dollars
    // segmented toggle on the right (like the reference's control cluster).
    const cardHeader = (
        <div className="flex items-center justify-between gap-3 px-4 pt-4 md:px-5">
            <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold tracking-tight text-white">Monthly Performance</h3>
                <span className="hidden rounded-md bg-slate-800/70 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-slate-400 sm:inline">{label}</span>
            </div>
            <div className="flex items-center gap-2.5">
                {perf?.pending > 0 && (
                    <span className="hidden items-center gap-1 text-[10px] font-mono text-amber-400/80 sm:flex">
                        <span className="h-1 w-1 rounded-full bg-amber-400/80" />
                        {perf.pending} pending
                    </span>
                )}
                {onDisplayChange && (
                    <div className="flex items-center overflow-hidden rounded-lg border border-slate-700/70 bg-slate-950/40">
                        {[{ key: 'units', label: 'Units' }, { key: 'dollars', label: 'Dollars' }].map((opt) => (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => opt.key !== display && onDisplayChange(opt.key)}
                                aria-pressed={display === opt.key}
                                className={`px-2.5 py-1 text-[10px] font-mono font-semibold transition-colors ${
                                    display === opt.key ? 'bg-emerald-500/15 text-emerald-300' : 'text-slate-500 hover:text-slate-300'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    if (isLoading) {
        return (
            <div className={shell}>
                {cardHeader}
                <div className="grid gap-5 p-4 md:grid-cols-2 md:p-5">
                    <div className="flex animate-pulse flex-col gap-4">
                        <div className="h-7 w-40 rounded-lg bg-slate-800" />
                        <div className="h-14 w-52 rounded-lg bg-slate-800" />
                    </div>
                    <div className="h-[140px] animate-pulse rounded-xl bg-slate-800/60" />
                </div>
                <div className="grid grid-cols-2 border-t border-slate-800 divide-x divide-slate-800 sm:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex animate-pulse flex-col gap-2 px-4 py-3.5">
                            <div className="h-2 w-10 rounded bg-slate-800" />
                            <div className="h-4 w-14 rounded bg-slate-800" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={shell}>
                {cardHeader}
                <div className="flex flex-wrap items-center gap-3 px-4 py-8 md:px-5">
                    <span className="text-xs font-mono text-slate-500">
                        {error.isAuthError ? 'Sign in again to see your results.' : "Couldn't load your results."}
                    </span>
                    {!error.isAuthError && (
                        <button
                            type="button"
                            onClick={onRetry}
                            className="flex items-center gap-1.5 rounded-lg border border-slate-700/70 px-2.5 py-1.5 text-[11px] font-mono font-medium text-slate-400 transition-all hover:border-slate-600 hover:bg-slate-800/60 hover:text-slate-200"
                        >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Retry
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Empty month — nothing tracked yet. Nudge toward the Results Tracker.
    if (!perf || (perf.totalBets ?? 0) === 0) {
        return (
            <div className={shell}>
                {cardHeader}
                <div className="flex flex-wrap items-center gap-3 px-4 py-8 md:px-5">
                    <span className="text-xs font-mono text-slate-500">No bets tracked in {label} yet</span>
                    <button
                        type="button"
                        onClick={() => navigate('/results-tracker')}
                        className="group/track flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-mono font-semibold text-emerald-300 transition-all hover:border-emerald-500/50 hover:bg-emerald-500/15"
                    >
                        Start tracking
                        <svg className="h-3 w-3 transition-transform group-hover/track:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={shell}>
            {cardHeader}

            <div className="grid px-4 py-4 md:px-5 md:py-5 lg:grid-cols-3 lg:items-stretch">
                {/* Headline: net P/L with ROI delta chip */}
                <div className="flex min-w-0 flex-col justify-center pb-4 text-center lg:pb-0 lg:pr-5">
                    <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500">Net Profit / Loss</span>
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
                        <span className={`text-4xl font-bold leading-none tracking-tight md:text-5xl ${pnlPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                            <ReelValue text={fmtSigned(pnl)} />
                        </span>
                        <span className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold tabular-nums ${
                            roiPositive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                            <TrendArrow trend={roiPositive ? 'up' : 'down'} className="h-3.5 w-3.5" />
                            {Math.abs(roi).toFixed(1)}%
                        </span>
                    </div>
                    <p className="mt-2.5 text-[11px] font-mono text-slate-500">
                        {Math.max((perf.totalBets ?? 0) - (perf.pending ?? 0), 0)} bets settled · ROI {roiPositive ? '+' : ''}{roi.toFixed(1)}%
                    </p>
                </div>

                {/* Cumulative P/L area chart */}
                <div className="min-w-0 border-t border-slate-800/70 py-4 lg:border-l lg:border-t-0 lg:px-5 lg:py-0">
                    <div className="mb-2 text-center text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Cumulative P/L</div>
                    <div className="relative h-[132px] w-full">
                        {series.length > 1 ? (
                            <PerformanceAreaChart data={series} positive={pnlPositive} format={fmtAxis} xLeft="Start" xRight="Now" />
                        ) : (
                            <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-800 text-[11px] font-mono text-slate-600">
                                Not enough settled days to chart yet
                            </div>
                        )}
                    </div>
                </div>

                {/* Daily P/L bar chart — one bar per day of the month */}
                <div className="min-w-0 border-t border-slate-800/70 pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
                    <div className="mb-2 text-center text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Daily P/L</div>
                    <div className="relative h-[132px] w-full">
                        {daily.values.length > 0 ? (
                            <DailyPnlChart
                                data={daily.values}
                                format={fmtAxis}
                                labelFormat={fmtBar}
                                xLeft={formatDayLabel(daily.dates[0]) || 'Start'}
                                xRight={formatDayLabel(daily.dates[daily.dates.length - 1]) || 'Now'}
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-800 text-[11px] font-mono text-slate-600">
                                No daily results yet
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Supporting metric strip */}
            <div className="grid grid-cols-2 border-t border-slate-800 divide-x divide-y divide-slate-800 sm:grid-cols-4 sm:divide-y-0">
                <PerfStat label="Bets" value={<ReelValue text={perf.totalBets} />} sub={`${perf.wins}-${perf.losses}`} />
                <PerfStat
                    label="Win Rate"
                    value={<ReelValue text={`${Math.round(winRate)}%`} />}
                />
                <PerfStat label="Staked" value={<ReelValue text={fmtFull(staked)} />} />
                <PerfStat
                    label="Returned"
                    value={<ReelValue text={fmtFull(returned)} />}
                    trend={returned >= staked ? 'up' : 'down'}
                />
            </div>
        </div>
    );
}

function formatMatchDate(match) {
    // Prefer the full kickoff timestamp (date + time) when available; fall back
    // to the date-only `start_time`, parsed at local noon to avoid TZ roll-over.
    let date;
    let hasTime = false;
    if (match?.kickoff_time) {
        date = new Date(match.kickoff_time);
        hasTime = true;
    } else if (match?.start_time) {
        date = new Date(`${match.start_time}T12:00:00`);
    } else {
        return null;
    }
    if (Number.isNaN(date.getTime())) return null;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((target - today) / 86400000);

    const weekday = date.toLocaleDateString([], { weekday: 'short' });
    const dayMonth = date.toLocaleDateString([], { day: 'numeric', month: 'short' });
    const time = hasTime ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;

    let relative = null;
    if (diffDays === 0) relative = 'Today';
    else if (diffDays === 1) relative = 'Tomorrow';
    else if (diffDays > 1 && diffDays < 7) relative = `In ${diffDays} days`;

    return { weekday, dayMonth, time, relative };
}

// A match counts as completed when its scheduled time is clearly in the past,
// or — for matches with no schedule data at all — when it has dropped out of
// the live odds feed. The hub only shows the current round, and completed
// games are the ones the odds feed no longer carries (kickoff_time null,
// bookmakers empty), which otherwise render as "TBC".
function isMatchCompleted(match) {
    const now = Date.now();
    if (match?.kickoff_time) {
        const t = new Date(match.kickoff_time).getTime();
        // ~3.5h covers a full game; anything past that has finished.
        if (!Number.isNaN(t)) return now > t + 3.5 * 3600000;
    }
    if (match?.start_time) {
        const endOfDay = new Date(`${match.start_time}T23:59:59`).getTime();
        if (!Number.isNaN(endOfDay)) return now > endOfDay;
    }
    return !(Array.isArray(match?.bookmakers) && match.bookmakers.length > 0);
}

function TeamCrest({ name, size = 'md' }) {
    const dims = size === 'lg' ? 'w-14 h-14' : 'w-11 h-11';
    return (
        <TeamGuernsey
            name={name}
            className={`${dims} flex-shrink-0 drop-shadow-[0_3px_8px_rgba(0,0,0,0.5)]`}
        />
    );
}

function MatchCard({ match, index, expanded, onToggle, onView }) {
    const homeMeta = getTeamMeta(match.home_team);
    const awayMeta = getTeamMeta(match.away_team);
    const dateInfo = formatMatchDate(match);
    const completed = isMatchCompleted(match);

    const bookmakers = (Array.isArray(match.bookmakers) ? [...match.bookmakers] : []).sort(
        (a, b) => bookmakerRank(a?.bookmaker) - bookmakerRank(b?.bookmaker)
            || bookmakerLabel(a).localeCompare(bookmakerLabel(b))
    );
    const hasOdds = bookmakers.length > 0;
    const visibleBooks = bookmakers.slice(0, MAX_VISIBLE_LOGOS);
    const hiddenBooks = bookmakers.length - visibleBooks.length;
    const distinctMarkets = (() => {
        const set = new Set();
        bookmakers.forEach(b => (b.markets || []).forEach(m => set.add(m)));
        return set.size;
    })();

    return (
        <div
            className={`group relative rounded-2xl border bg-gradient-to-b from-slate-900/80 to-slate-900/40 overflow-hidden transition-all duration-200 hub-reveal ${
                expanded
                    ? 'border-emerald-500/40 shadow-lg shadow-emerald-500/5'
                    : 'border-slate-800/80 hover:border-slate-700 hover:shadow-lg hover:shadow-black/30'
            }`}
            style={{ animationDelay: `${Math.min(index * 45, 360)}ms` }}
        >
            {/* Brand accent bar */}
            <div
                className="h-1 w-full opacity-80"
                style={{ background: `linear-gradient(90deg, ${homeMeta.to}, ${awayMeta.to})` }}
            />

            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className="w-full text-left px-4 pt-3.5 pb-1 focus:outline-none"
            >
                {/* Meta row */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1.5 text-[11px] font-mono text-slate-500">
                        <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="uppercase tracking-wider">{match.venue || 'Venue TBC'}</span>
                    </div>
                    {completed ? (
                        <div className="flex items-center gap-1.5">
                            <span className="px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-400 text-[10px] font-mono font-semibold uppercase tracking-wider">
                                Completed
                            </span>
                            {dateInfo && (
                                <span className="text-[11px] font-mono text-slate-500">
                                    {dateInfo.weekday} {dateInfo.dayMonth}
                                </span>
                            )}
                        </div>
                    ) : dateInfo ? (
                        <div className="flex items-center gap-1.5">
                            {dateInfo.relative && (
                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-mono font-semibold">
                                    {dateInfo.relative}
                                </span>
                            )}
                            <span className="text-[11px] font-mono text-slate-400">
                                {dateInfo.weekday} {dateInfo.dayMonth}
                                {dateInfo.time && <span className="text-slate-500"> · {dateInfo.time}</span>}
                            </span>
                        </div>
                    ) : (
                        <span className="text-[10px] font-mono text-slate-600 uppercase tracking-wider">Date TBC</span>
                    )}
                </div>

                {/* Teams */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                        <TeamCrest name={match.home_team} size="lg" />
                        <div className="text-center min-w-0 w-full">
                            <div className="text-sm font-semibold text-white leading-tight break-words">{match.home_team}</div>
                            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mt-0.5">Home</div>
                        </div>
                    </div>

                    <div className="flex flex-col items-center gap-1 px-1">
                        <span className="text-[10px] font-mono font-bold text-slate-600 tracking-widest">VS</span>
                        <div className="w-7 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
                    </div>

                    <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                        <TeamCrest name={match.away_team} size="lg" />
                        <div className="text-center min-w-0 w-full">
                            <div className="text-sm font-semibold text-white leading-tight break-words">{match.away_team}</div>
                            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mt-0.5">Away</div>
                        </div>
                    </div>
                </div>

                {/* Odds availability strip */}
                <div className="mt-4 pt-3 border-t border-slate-800/70">
                    {hasOdds ? (
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center">
                                {visibleBooks.map((b, i) => (
                                    <div
                                        key={b.bookmaker}
                                        className="w-6 h-6 rounded-md bg-slate-800 ring-2 ring-slate-900 overflow-hidden flex items-center justify-center -ml-1.5 first:ml-0"
                                        style={{ zIndex: visibleBooks.length - i }}
                                        title={bookmakerLabel(b)}
                                    >
                                        <BookmakerLogo bookmaker={b.bookmaker} size={24} />
                                    </div>
                                ))}
                                {hiddenBooks > 0 && (
                                    <div className="w-6 h-6 rounded-md bg-slate-800 ring-2 ring-slate-900 flex items-center justify-center -ml-1.5 text-[9px] font-mono font-semibold text-slate-400">
                                        +{hiddenBooks}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                                <span>{bookmakers.length} {bookmakers.length === 1 ? 'book' : 'books'}</span>
                                <span className="text-slate-700">·</span>
                                <span>{distinctMarkets} {distinctMarkets === 1 ? 'market' : 'markets'}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="text-[10px] font-mono text-slate-600 uppercase tracking-wider text-center py-0.5">
                            {completed ? 'Game completed' : 'No live odds yet'}
                        </div>
                    )}
                </div>

                {/* Expand affordance */}
                <div className="mt-3 flex items-center justify-center gap-1.5">
                    <span className="text-[11px] font-mono text-slate-500 group-hover:text-slate-400 transition-colors">
                        {expanded ? 'Hide markets' : 'View markets'}
                    </span>
                    <svg
                        className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>

            {/* Card footer — jump to the full match breakdown */}
            <div className="px-4 pb-3.5 flex items-center justify-end">
                <button
                    type="button"
                    onClick={onView}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/15 hover:border-emerald-500/50 transition-all"
                >
                    View Match
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Expanded detail — per-bookmaker market coverage */}
            {expanded && (
                <div className="px-4 pb-4 animate-fade-in">
                    {hasOdds ? (
                        <div className="rounded-xl border border-slate-800/80 bg-slate-950/40 p-3.5">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Bookmakers &amp; Markets</span>
                                <span className="text-[10px] font-mono text-slate-600">{distinctMarkets} markets</span>
                            </div>
                            <div className="space-y-2.5">
                                {bookmakers.map((b) => (
                                    <div key={b.bookmaker} className="flex items-start gap-2.5">
                                        <div className="flex items-center gap-1.5 w-[88px] flex-shrink-0 pt-0.5">
                                            <BookmakerLogo bookmaker={b.bookmaker} size={18} />
                                            <span className="text-[11px] font-medium text-slate-300 truncate">{bookmakerLabel(b)}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                                            {(b.markets || []).length > 0 ? (
                                                b.markets.map((m) => (
                                                    <span key={m} className="px-1.5 py-0.5 rounded bg-slate-800/70 border border-slate-700/50 text-[10px] font-mono text-slate-400">
                                                        {m}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-[10px] font-mono text-slate-600">No markets listed</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : completed ? (
                        <div className="rounded-xl border border-slate-800/80 bg-slate-950/40 p-5 flex flex-col items-center text-center">
                            <div className="w-10 h-10 rounded-xl bg-slate-800/60 border border-slate-700/50 flex items-center justify-center mb-3">
                                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-sm font-semibold text-white mb-1">Game completed</p>
                            <p className="text-xs font-mono text-slate-500 leading-relaxed max-w-xs">
                                {match.home_team} v {match.away_team} has finished — bookmaker markets are no longer available.
                            </p>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-slate-800/80 bg-slate-950/40 p-5 flex flex-col items-center text-center">
                            <div className="w-10 h-10 rounded-xl bg-slate-800/60 border border-slate-700/50 flex items-center justify-center mb-3">
                                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                            </div>
                            <p className="text-sm font-semibold text-white mb-1">No live odds yet</p>
                            <p className="text-xs font-mono text-slate-500 leading-relaxed max-w-xs">
                                Bookmaker markets for {match.home_team} v {match.away_team} will appear here once odds open.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function SnipeHub() {
    const canUsePropTool = true;
    const [sport, setSport] = useState('afl');
    const [hubData, setHubData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const navigate = useNavigate();

    // Seconds until the next automatic refresh (drives the wheel timer).
    const [countdown, setCountdown] = useState(REFRESH_SECONDS);
    const countdownRef = useRef(null);
    const refreshRef = useRef(null);

    // Background (showLoading = false) refreshes hydrate silently: no spinner,
    // and a transient failure keeps the last good matches on screen.
    const fetchMatches = useCallback(async (showLoading = false) => {
        if (showLoading) {
            setIsLoading(true);
            setError(null);
        }

        const result = await snipeHubService.getAflMatches();

        if (result.success) {
            setHubData(result.data);
            setError(null);
        } else {
            // Stop polling when the failure can't be fixed by retrying.
            if (result.isAuthError) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (refreshRef.current) clearInterval(refreshRef.current);
            }
            if (showLoading) {
                setHubData(null);
                setError(result);
            }
        }

        setIsLoading(false);
    }, []);

    // (Re)start the next-refresh wheel + the auto-refresh interval.
    const startTimers = useCallback(() => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshRef.current) clearInterval(refreshRef.current);
        setCountdown(REFRESH_SECONDS);
        countdownRef.current = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? REFRESH_SECONDS : prev - 1));
        }, 1000);
        refreshRef.current = setInterval(() => {
            fetchMatches(false);
        }, REFRESH_SECONDS * 1000);
    }, [fetchMatches]);

    useEffect(() => {
        fetchMatches(true);
        startTimers();
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
            if (refreshRef.current) clearInterval(refreshRef.current);
        };
    }, [sport, fetchMatches, startTimers]);

    const handleRefresh = useCallback(() => {
        fetchMatches(true);
        startTimers();
    }, [fetchMatches, startTimers]);

    const matches = useMemo(() => {
        const list = hubData?.matches || [];
        // Order by kickoff time (then date-only, then id) so the soonest game
        // leads. Matches without any time sink to the bottom.
        const ts = (m) => {
            if (m?.kickoff_time) return new Date(m.kickoff_time).getTime();
            if (m?.start_time) return new Date(`${m.start_time}T12:00:00`).getTime();
            return Number.POSITIVE_INFINITY;
        };
        return [...list].sort((a, b) => {
            const diff = ts(a) - ts(b);
            if (diff !== 0 && !Number.isNaN(diff)) return diff;
            return (a?.match_id || '').localeCompare(b?.match_id || '');
        });
    }, [hubData]);
    const roundLabel = hubData?.round || null;
    const matchCount = hubData?.count ?? matches.length;

    // Soonest match that hasn't kicked off yet — surfaced in the hero context row.
    const nextMatch = useMemo(() => {
        const now = Date.now();
        const ts = (m) => {
            if (m?.kickoff_time) return new Date(m.kickoff_time).getTime();
            if (m?.start_time) return new Date(`${m.start_time}T12:00:00`).getTime();
            return NaN;
        };
        return matches.find((m) => {
            const t = ts(m);
            return !Number.isNaN(t) && t > now;
        }) || null;
    }, [matches]);
    const nextMatchDate = useMemo(() => (nextMatch ? formatMatchDate(nextMatch) : null), [nextMatch]);

    // Current-month performance — backend-computed summary (see
    // monthly_performance_api.md). Refetched when the denomination changes;
    // the user's units/dollars preference persists across sessions.
    const [monthPerf, setMonthPerf] = useState(null);
    const [monthPerfLoading, setMonthPerfLoading] = useState(true);
    const [monthPerfError, setMonthPerfError] = useState(null);
    const [perfDisplay, setPerfDisplay] = useState(() => {
        try {
            const saved = localStorage.getItem('snipeHubPerfDisplay');
            return saved === 'dollars' ? 'dollars' : 'units';
        } catch {
            return 'units';
        }
    });

    const fetchMonthPerf = useCallback(async () => {
        setMonthPerfLoading(true);
        setMonthPerfError(null);

        const result = await resultsTrackerService.getMonthlyPerformance({ display: perfDisplay });

        if (result.success) {
            setMonthPerf(result.data);
        } else {
            setMonthPerf(null);
            setMonthPerfError(result);
        }
        setMonthPerfLoading(false);
    }, [perfDisplay]);

    useEffect(() => {
        fetchMonthPerf();
    }, [fetchMonthPerf]);

    const handlePerfDisplayChange = useCallback((next) => {
        setPerfDisplay(next);
        try { localStorage.setItem('snipeHubPerfDisplay', next); } catch { /* storage unavailable */ }
    }, []);

    const handleToggle = (id) => {
        setExpandedId(prev => (prev === id ? null : id));
    };

    // Hand the full match object (incl. bookmakers) to the match page so it
    // renders instantly; the page re-fetches only on a direct/refresh load.
    const handleViewMatch = (match) => {
        navigate(`/snipe-hub/match/${match.match_id}`, {
            state: { match, round: roundLabel },
        });
    };

    const renderErrorState = () => {
        const errorConfig = {
            AUTH: { icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636', color: 'text-red-400', bg: 'bg-red-500/5 border-red-500/10', title: 'Authentication Failed' },
            RATE_LIMIT_EXCEEDED: { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-amber-400', bg: 'bg-amber-500/5 border-amber-500/10', title: 'Too Many Requests' },
            SERVICE_UNAVAILABLE: { icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-slate-400', bg: 'bg-slate-500/5 border-slate-500/10', title: 'Service Unavailable' },
            NETWORK_ERROR: { icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-red-400', bg: 'bg-red-500/5 border-red-500/10', title: 'Connection Error' },
        };

        const code = error.isAuthError ? 'AUTH' : (error.errorCode || 'NETWORK_ERROR');
        const config = errorConfig[code] || errorConfig.NETWORK_ERROR;
        const showRetry = ['SERVICE_UNAVAILABLE', 'NETWORK_ERROR', 'SERVER_ERROR'].includes(error.errorCode);

        return (
            <div className="flex items-center justify-center py-24 px-6">
                <div className="flex flex-col items-center text-center max-w-sm">
                    <div className={`w-12 h-12 rounded-lg ${config.bg} border flex items-center justify-center mb-4`}>
                        <svg className={`w-6 h-6 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={config.icon} />
                        </svg>
                    </div>
                    <h4 className={`text-sm font-semibold mb-1.5 ${config.color}`}>{config.title}</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{error.message}</p>
                    {showRetry && (
                        <button
                            onClick={handleRefresh}
                            className="mt-5 px-4 py-2 text-xs font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all flex items-center gap-2"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Retry
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderEmptyState = () => (
        <div className="flex items-center justify-center py-24 px-6">
            <div className="flex flex-col items-center text-center max-w-sm">
                <div className="w-14 h-14 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center mb-5">
                    <svg className="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>
                <h4 className="text-base font-semibold text-white mb-2">No matches scheduled</h4>
                <p className="text-sm font-mono text-slate-500 leading-relaxed">
                    {roundLabel
                        ? `There are no matches for ${roundLabel} right now. Check back when the next round is locked in.`
                        : 'There are no upcoming matches right now. Check back soon.'}
                </p>
            </div>
        </div>
    );

    const renderLoading = () => (
        <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 flex flex-col items-center justify-center gap-3 py-24">
            <div className="w-8 h-8 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin" />
            <span className="text-xs font-mono text-slate-500">Loading matches…</span>
        </div>
    );

    return (
        <div className="min-h-[calc(100vh-72px)] w-full overflow-y-auto styled-scrollbar">
            <div className="relative max-w-[1280px] mx-auto px-3 md:px-6 py-4 md:py-6">

                {/* Hero: greeting bar with a sport switch + a next-match
                    quick-link, above the monthly performance card. */}
                <header className="mb-5 hub-reveal">
                    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-400/90">
                                <span className="relative flex h-1.5 w-1.5 shrink-0">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                </span>
                                <span className="truncate">Live</span>
                            </div>
                            <h1 className="mt-1 text-2xl font-bold tracking-tight text-white md:text-3xl">
                                Snipe Hub
                            </h1>
                        </div>

                        <div className="flex items-center gap-2">
                            {nextMatch && (
                                <button
                                    type="button"
                                    onClick={() => handleViewMatch(nextMatch)}
                                    title={`Next: ${nextMatch.home_team} vs ${nextMatch.away_team}`}
                                    className="group/next hidden items-center gap-2 rounded-full border border-slate-700 bg-slate-800/60 py-1.5 pl-3 pr-3 transition-colors hover:border-emerald-500/40 sm:flex"
                                >
                                    <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-slate-500">Next</span>
                                    <span className="flex items-center gap-1">
                                        <TeamGuernsey name={nextMatch.home_team} className="h-4 w-4 shrink-0" />
                                        <span className="text-[10px] text-slate-600">v</span>
                                        <TeamGuernsey name={nextMatch.away_team} className="h-4 w-4 shrink-0" />
                                    </span>
                                    {nextMatchDate && (
                                        <span className="text-[11px] font-mono text-slate-400">
                                            {nextMatchDate.relative || nextMatchDate.weekday}
                                            {nextMatchDate.time && <span className="text-slate-600"> {nextMatchDate.time}</span>}
                                        </span>
                                    )}
                                    <svg className="h-3 w-3 text-slate-500 transition-all group-hover/next:translate-x-0.5 group-hover/next:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            )}

                            {/* Sport switch — dropdown chip */}
                            <div className="relative inline-flex items-center rounded-full border border-slate-700 bg-slate-800/60 py-1.5 pl-4 pr-8 transition-colors hover:border-slate-600">
                                <select
                                    value={sport}
                                    onChange={(e) => setSport(e.target.value)}
                                    aria-label="Select sport"
                                    className="cursor-pointer appearance-none bg-transparent text-xs font-semibold uppercase tracking-wide text-slate-200 focus:outline-none"
                                >
                                    {SPORT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value} className="bg-slate-900 normal-case tracking-normal">{opt.label}</option>
                                    ))}
                                </select>
                                <svg className="pointer-events-none absolute right-3 h-3 w-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <MonthlyPerformanceTerminal
                        perf={monthPerf}
                        isLoading={monthPerfLoading}
                        error={monthPerfError}
                        onRetry={fetchMonthPerf}
                        display={perfDisplay}
                        onDisplayChange={handlePerfDisplayChange}
                    />
                </header>

                {/* Section title — stacks above the controls on mobile so the
                    row isn't cramped; single row from md up. */}
                <div className="relative flex flex-col gap-2.5 mb-3.5 px-0.5 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2.5">
                        <h2 className="text-sm font-semibold text-white tracking-tight">Upcoming Matches</h2>
                        {!isLoading && !error && hubData && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-800/60 rounded text-[11px] font-mono">
                                <span className="text-white font-semibold">{matchCount}</span>
                                <span className="text-slate-600">games</span>
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        {canUsePropTool && (
                            <button
                                onClick={() => navigate('/prop-tool')}
                                className="flex flex-1 items-center justify-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/15 hover:border-emerald-500/50 transition-all md:flex-none md:justify-start"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                                </svg>
                                Open Player Prop Tool
                            </button>
                        )}
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono font-medium text-slate-400 border border-slate-700/70 rounded-lg hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all disabled:opacity-50"
                        >
                            <svg className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh
                        </button>
                        {!isLoading && !error && <CountdownRing countdown={countdown} total={REFRESH_SECONDS} />}
                    </div>
                </div>

                {/* Content */}
                <div className="relative">
                    {isLoading && !hubData && renderLoading()}
                    {error && renderErrorState()}
                    {!error && hubData && matches.length === 0 && renderEmptyState()}
                    {!error && matches.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                            {matches.map((match, i) => (
                                <MatchCard
                                    key={match.match_id}
                                    match={match}
                                    index={i}
                                    expanded={expandedId === match.match_id}
                                    onToggle={() => handleToggle(match.match_id)}
                                    onView={() => handleViewMatch(match)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                @keyframes hubReveal {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .hub-reveal {
                    animation: hubReveal 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
                }
            `}</style>
        </div>
    );
}

export default SnipeHub;
